import React from 'react';
import styled, { keyframes } from 'styled-components';
import SectionHeader from '../common/SectionHeader';
import Button from '../common/Button';
import Container from '../common/Container';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faShoppingCart, faPlus, faGem } from '@fortawesome/free-solid-svg-icons';

// ✅ Animação para o "efeito diamante" (brilho a passar)
const diamondShimmer = keyframes`
    0% {
        transform: translateX(-150%) skewX(-30deg);
    }
    100% {
        transform: translateX(150%) skewX(-30deg);
    }
`;

const PricingWrapper = styled.section`
    padding: 120px 0;
    background-color: ${({ theme }) => theme.colors.light};
`;

const PricingGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 30px;
    margin-top: 60px;
`;

const PricingCard = styled.div`
    background: ${({ theme }) => theme.colors.lighter};
    border-radius: ${({ theme }) => theme.radii.lg};
    padding: 40px 30px;
    box-shadow: ${({ theme }) => theme.shadows.sm};
    transition: ${({ theme }) => theme.transitions.default};
    position: relative;
    overflow: hidden;
    border: 1px solid rgba(0, 0, 0, 0.05);

    &.featured {
        border: 2px solid ${({ theme }) => theme.colors.primary};
        transform: translateY(-10px);

        @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
            transform: translateY(0);
        }

        &::before {
            content: 'MAIS POPULAR';
            position: absolute;
            top: 20px;
            right: -30px;
            background: ${({ theme }) => theme.colors.primary};
            color: white;
            padding: 5px 30px;
            transform: rotate(45deg);
            font-size: 12px;
            font-weight: 700;
        }
    }
`;

const PricingCardHeader = styled.div`
    text-align: center;
    margin-bottom: 30px;
`;

const PricingCardTitle = styled.h3`
    font-size: 22px;
    font-weight: 800;
    margin-bottom: 10px;
    color: ${({ theme }) => theme.colors.dark};
`;

const PricingCardOrigin = styled.p`
    font-size: 14px;
    color: ${({ theme }) => theme.colors.gray};
    margin-bottom: 20px;
`;

const PricingCardPrice = styled.div`
    font-size: 48px;
    font-weight: 800;
    color: ${({ theme }) => theme.colors.primary};
    margin-bottom: 5px;
    line-height: 1;

    small {
        font-size: 16px;
        font-weight: 600;
        color: ${({ theme }) => theme.colors.gray};
    }
`;

const PricingCardFeatures = styled.ul`
    list-style: none;
    margin-bottom: 30px;
    padding: 0;

    li {
        margin-bottom: 12px;
        display: flex;
        align-items: center;
        gap: 10px;
        color: ${({ theme }) => theme.colors.dark};

        svg {
            color: ${({ theme }) => theme.colors.success};
        }
    }
`;

const PricingButton = styled.a`
    display: block;
    width: 100%;
    text-align: center;
    padding: 16px;
    border-radius: ${({ theme }) => theme.radii.md};
    color: white;
    font-weight: 700;
    text-decoration: none;
    transition: ${({ theme }) => theme.transitions.default};
    border: none;
    cursor: pointer;
    background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary} 0%, ${({ theme }) => theme.colors.accent} 100%);
    box-shadow: 0 4px 15px rgba(108, 92, 231, 0.3);

    &:hover {
        transform: translateY(-3px);
        box-shadow: 0 8px 25px rgba(108, 92, 231, 0.4);
    }

    &.secondary {
        background: white;
        color: ${({ theme }) => theme.colors.primary};
        border: 1px solid ${({ theme }) => theme.colors.lightGray};
        box-shadow: ${({ theme }) => theme.shadows.sm};

        &:hover {
            border-color: ${({ theme }) => theme.colors.primaryLight};
            transform: translateY(-3px);
            box-shadow: ${({ theme }) => theme.shadows.md};
        }
    }
`;

const UpsellBanner = styled.div`
    background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary} 0%, ${({ theme }) => theme.colors.accent} 100%);
    color: white;
    padding: 30px 40px;
    border-radius: ${({ theme }) => theme.radii.md};
    margin-top: 60px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 20px;

    @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
        flex-direction: column;
        text-align: center;
    }
`;

const AnimatedGradientButton = styled(Button)`
    position: relative;
    overflow: hidden;
    background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary} 0%, ${({ theme }) => theme.colors.accent} 100%);
    
    &::after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 40%;
        height: 100%;
        background: linear-gradient(
            to right,
            rgba(255, 255, 255, 0) 0%,
            rgba(255, 255, 255, 0.4) 50%,
            rgba(255, 255, 255, 0) 100%
        );
        transform: translateX(-150%) skewX(-30deg);
        animation: ${diamondShimmer} 3s linear infinite;
        animation-delay: 1.5s;
    }
`;


const PricingSection = () => {
    const mainPlans = [
        { title: "Consulta Completa", origin: "API Brasil", price: "39,90", featured: true, features: ["Roubo/Furto", "Débitos (IPVA, multas)", "Histórico de proprietários", "Restrições e recalls", "Dados técnicos completos"] },
        { title: "BIN Federal", origin: "Assertiva", price: "49,90", features: ["Consulta de dados básicos", "Informações do DETRAN", "Situação do veículo", "Compatível com consulta completa"] },
        { title: "BIN Federal + Roubo/Furto", origin: "Assertiva", price: "59,90", features: ["Todos os benefícios do BIN Federal", "Verificação de roubo/furto", "Alertas de irregularidades", "Compatível com consulta completa"] }
    ];
    const additionalPlans = [
        { title: "Decodificador de Chassi", origin: "Assertiva", price: "29,90", features: ["Informações detalhadas do chassi", "Origem e especificações", "Compatível com consulta completa"] },
        { title: "Histórico de Leilão", origin: "Assertiva", price: "44,90", features: ["Verificação de passagem por leilão", "Detalhes do leilão", "Motivo do leilão", "Compatível com consulta completa"] },
        { title: "Precificador / FIPE", origin: "Assertiva", price: "19,90", features: ["Valor FIPE atualizado", "Análise de mercado", "Compatível com consulta completa"] }
    ];

    const renderPlan = (plan, index) => (
        <PricingCard className={plan.featured ? 'featured' : ''} key={index}>
            <PricingCardHeader>
                <PricingCardTitle>{plan.title}</PricingCardTitle>
                <PricingCardOrigin>{plan.origin}</PricingCardOrigin>
                <PricingCardPrice>R$ {plan.price}</PricingCardPrice>
            </PricingCardHeader>
            <PricingCardFeatures>
                {plan.features.map((feat, i) => <li key={i}><FontAwesomeIcon icon={faCheck} /> {feat}</li>)}
            </PricingCardFeatures>
            <PricingButton className={!plan.featured ? 'secondary' : ''}>
                <FontAwesomeIcon icon={plan.featured ? faShoppingCart : faPlus} />
                {plan.featured ? 'Comprar Agora' : 'Adicionar à Consulta'}
            </PricingButton>
        </PricingCard>
    );

    return (
        <PricingWrapper id="planos">
            <Container>
                <SectionHeader title="Nossos Planos e Preços" subtitle="Escolha o serviço que melhor atende às suas necessidades" />
                <PricingGrid>{mainPlans.map(renderPlan)}</PricingGrid>
                <UpsellBanner>
                    <div>
                        <h3>Pacote Completo</h3>
                        <p>Adquira a Consulta Completa + BIN Federal + Roubo/Furto e economize 15%</p>
                    </div>
                    <AnimatedGradientButton>
                        <FontAwesomeIcon icon={faGem} /> Quero o Pacote Completo
                    </AnimatedGradientButton>
                </UpsellBanner>
                <PricingGrid>{additionalPlans.map(renderPlan)}</PricingGrid>
            </Container>
        </PricingWrapper>
    );
};

export default PricingSection;
